/** The class both declaration editors (phase-1 block editor, phase-2 template
 *  editor) scope `declaration_v2.css` under; the element carrying it is the
 *  grey screen backdrop the stylesheet's `@media screen` rules paint. */
export const PRINT_CSS_SCOPE = "tpl-scope"

/** Prefix every selector in a stylesheet with `scope`, so a stylesheet written
 *  for a whole document (`declaration_v2.css`: `html, body`, bare `p`,
 *  `table`, `:root` variables…) can be injected into the app page and only
 *  ever style what sits inside the scope element. Root-level selectors
 *  (`:root`, `html`, `body`) become the scope element itself, so the custom
 *  properties and the screen-preview backdrop land on it. `@font-face` and
 *  `@page` blocks carry no selectors and pass through; other at-rules
 *  (`@media`) are recursed into. Comments pass through untouched.
 *
 *  A brace-depth tokenizer rather than the CSSOM: jsdom lacks
 *  `CSSStyleSheet.replaceSync`, and the input is our own known stylesheet,
 *  not arbitrary CSS. */
export function scopeCss(css: string, scope: string): string {
  return walk(css, scope)
}

const ROOT_SELECTORS = new Set([":root", "html", "body"])

function scopeSelectorList(prelude: string, scope: string): string {
  const leading = prelude.match(/^\s*/)?.[0] ?? ""
  const trailing = prelude.match(/\s*$/)?.[0] ?? ""
  const list = prelude.trim()
  if (!list) return prelude
  const scoped = list
    .split(",")
    .map((sel) => sel.trim())
    .map((sel) => {
      if (ROOT_SELECTORS.has(sel)) return scope
      if (sel === scope || sel.startsWith(`${scope} `)) return sel
      return `${scope} ${sel}`
    })
  // `html, body` both become the scope — once.
  return `${leading}${Array.from(new Set(scoped)).join(", ")}${trailing}`
}

function walk(css: string, scope: string): string {
  let out = ""
  let i = 0
  while (i < css.length) {
    // Whitespace and comments between rules pass through verbatim — and must
    // never become part of the next prelude: a comment's commas would split
    // it into garbage selectors and the browser drops the whole rule (this
    // once swallowed the entire `@media screen` block).
    const ch = css[i]
    if (ch === " " || ch === "\n" || ch === "\t" || ch === "\r") {
      out += ch
      i += 1
      continue
    }
    if (css.startsWith("/*", i)) {
      const end = css.indexOf("*/", i + 2)
      const stop = end === -1 ? css.length : end + 2
      out += css.slice(i, stop)
      i = stop
      continue
    }
    const open = css.indexOf("{", i)
    if (open === -1) {
      out += css.slice(i)
      break
    }
    const prelude = css.slice(i, open)
    const close = matchingBrace(css, open)
    const body = css.slice(open + 1, close)
    const trimmed = prelude.trim()
    if (trimmed.startsWith("@")) {
      const passThrough =
        trimmed.startsWith("@font-face") || trimmed.startsWith("@page")
      out += `${prelude}{${passThrough ? body : walk(body, scope)}}`
    } else {
      out += `${scopeSelectorList(prelude, scope)}{${body}}`
    }
    i = close + 1
  }
  return out
}

function matchingBrace(css: string, open: number): number {
  let depth = 0
  for (let i = open; i < css.length; i++) {
    const ch = css[i]
    if (ch === "{") depth++
    else if (ch === "}") {
      depth--
      if (depth === 0) return i
    }
  }
  return css.length
}
